import { type FC, useState } from 'react';

import { Maximize2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { openLightbox } from '@shared/lib/openLightbox';
import { tokens } from '@shared/styles/tokens';
import type { IEquipmentItem } from '@shared/types';
import { Picture } from '@shared/ui/Picture';
import { VideoPlayer } from '@shared/ui/VideoPlayer';

import { CatalogList } from './CatalogList';
import styles from './CatalogSection.module.css';
import { EquipmentComposition } from './EquipmentComposition';
import { ExerciseGuide } from './ExerciseGuide';
import { TrainerSetup } from './TrainerSetup';

export interface CatalogSectionProps {
  /** Полное название раздела — заголовок страницы и «назад» */
  title: string;
  /** Короткое название для подписи раздела «Каталог · …» */
  shortTitle?: string;
  /** Путь раздела без слэша на конце, например /catalog/trainers */
  basePath: string;
  items: readonly IEquipmentItem[];
  /** Общий вид раздела над списком позиций */
  overviewImage?: string;
  /** Размеры файла общего вида — браузер резервирует место, список под картинкой не прыгает */
  overviewSize?: { width: number; height: number };
}

// Размер изображения без env(): браузер выбирает вариант под текущую схему карточки.
const mainPhotoSizes = (split: boolean, overview: boolean): string => {
  const padding = tokens.space.fluid(24, 4, 64);
  const page = `min(100vw, ${tokens.layout.contentMax})`;
  if (overview) return `calc(${page} - 2 * ${padding})`;
  return `(max-width: ${tokens.breakpoints.tablet}) calc(100vw - 2 * ${padding}), ${
    split ? `calc(${page} * 0.45 - 2 * ${padding})` : `calc((${page} - 3 * ${padding}) * 0.45)`
  }`;
};

// Обычный клик — SPA-переход с прокруткой наверх; колёсико/Ctrl+клик открывают ссылку в новой вкладке без onClick
// Явный behavior: 'smooth' не гасится CSS-правилом prefers-reduced-motion — проверяем сами
const scrollToTop = () =>
  window.scrollTo({
    top: 0,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });

/** Раздел каталога: список позиций или карточка позиции по :id из URL */
export const CatalogSection: FC<CatalogSectionProps> = (props) => {
  const { id } = useParams<{ id: string }>();
  const selectedItem = id ? props.items.find((i) => i.id === id) : undefined;

  return (
    <div className={styles.pageRoot}>
      {id && !selectedItem ? (
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Оборудование не найдено</h1>
          <Link className={styles.backButton} to={props.basePath}>
            Вернуться в раздел
          </Link>
        </div>
      ) : selectedItem ? (
        // key сбрасывает выбранное фото галереи при переходе к другой позиции
        <Detail key={selectedItem.id} item={selectedItem} {...props} />
      ) : (
        <Overview {...props} />
      )}
    </div>
  );
};

const Overview: FC<CatalogSectionProps> = ({
  title,
  shortTitle,
  basePath,
  items,
  overviewImage,
  overviewSize,
}) => (
  <>
    <div className={styles.pageHeader}>
      <div className={styles.badge}>Каталог · {shortTitle ?? title}</div>
      <h1 className={styles.pageTitle}>{title}</h1>
    </div>

    {overviewImage && (
      <div className={styles.overview}>
        {/* Общий вид в превью мелкий — открываем на весь экран с зумом */}
        <button
          className={styles.overviewButton}
          type="button"
          aria-label={`${title}: открыть общий вид на весь экран`}
          onClick={() => void openLightbox([overviewImage], 0, `${title} — общий вид`)}
        >
          <Picture
            className={styles.overviewImg}
            sizes={`min(960px, calc(100vw - 2 * ${tokens.space.pageBase}))`}
            src={overviewImage}
            alt={title}
            width={overviewSize?.width}
            height={overviewSize?.height}
            priority
          />
          <span className={styles.overviewHint} aria-hidden>
            <Maximize2 />
          </span>
        </button>
      </div>
    )}

    {items.length === 0 ? (
      <div className={styles.emptyState}>Раздел пополняется</div>
    ) : (
      <CatalogList items={items} basePath={basePath} />
    )}
  </>
);

interface DetailProps extends CatalogSectionProps {
  item: IEquipmentItem;
}

const Detail: FC<DetailProps> = ({ item, title, basePath }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = item.photos.slice(0, 4);
  const mainPhoto = photos[photoIndex] ?? photos[0];
  const exercises = item.exercises ?? [];
  // Правая колонка нужна, только если есть что в неё положить
  const setup = item.setup ?? [];
  const hasGuide = setup.length > 0 || exercises.length > 0 || Boolean(item.videoUrl);
  const composition = item.composition ?? [];
  const isOverview = composition.length > 0;

  const summary = (
    <>
      <p className={styles.detailDesc}>{item.description}</p>

      {item.specs && item.specs.length > 0 && (
        <dl className={styles.specsTable}>
          {item.specs.map(({ label, value }) => (
            <div className={styles.specRow} key={label}>
              <dt className={styles.specLabel}>{label}</dt>
              <dd className={styles.specValue}>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* Форма запроса КП живёт на странице контактов */}
      <Link className={styles.ctaLink} to="/contacts" onClick={scrollToTop}>
        Запросить КП
      </Link>
    </>
  );

  return (
    <>
      <nav className={styles.categoryNav} aria-label="Раздел каталога">
        <Link className={styles.categoryLink} to={basePath} onClick={scrollToTop}>
          <span className={styles.categoryTitle}>{title}</span>
          <span className={styles.categoryHint}>Вернуться к списку</span>
        </Link>
      </nav>
      <div className={styles.detailLayout} data-split={hasGuide}>
        <div
          className={styles.productPanel}
          data-solo={!hasGuide && !isOverview}
          data-overview={isOverview}
        >
          <h1 className={styles.detailTitle}>{item.title}</h1>

          <div className={styles.media}>
            {mainPhoto ? (
              // Клик открывает фото на весь экран с зумом
              <button
                className={styles.mainPhoto}
                type="button"
                data-zoomable="true"
                aria-label={`${item.title}: открыть фото на весь экран`}
                onClick={() => void openLightbox(photos, photoIndex, item.title)}
              >
                <Picture
                  className={styles.mainPhotoImg}
                  src={mainPhoto}
                  alt={item.title}
                  sizes={mainPhotoSizes(hasGuide, isOverview)}
                  priority
                />
                <span className={styles.zoomHint} aria-hidden>
                  <Maximize2 />
                </span>
              </button>
            ) : (
              <div className={styles.mainPhoto}>Фото оборудования</div>
            )}
            {photos.length > 1 && (
              <div className={styles.thumbs}>
                {photos.map((p, i) => (
                  <button
                    className={styles.thumb}
                    key={p}
                    type="button"
                    aria-pressed={i === photoIndex}
                    aria-label={`Фото ${i + 1} из ${photos.length}`}
                    onClick={() => setPhotoIndex(i)}
                  >
                    <Picture
                      className={styles.thumbImg}
                      sizes="clamp(63px, 6vw, 80px)"
                      src={p}
                      alt=""
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.info} data-overview={isOverview}>
            {isOverview ? (
              <>
                <div className={styles.overviewSummary}>{summary}</div>
                <EquipmentComposition id={item.id} items={composition} basePath={basePath} />
              </>
            ) : (
              summary
            )}
          </div>
        </div>

        {hasGuide && (
          <div className={styles.guidePanel}>
            {setup.length > 0 && <TrainerSetup id={item.id} steps={setup} />}

            {exercises.length > 0 && (
              <ExerciseGuide
                id={item.id}
                exercises={exercises}
                safetyNote={item.safetyNote}
                hasVideo={Boolean(item.videoUrl)}
              />
            )}

            {item.videoUrl && (
              <div className={styles.video}>
                <div className={styles.videoLabel}>Видео: {item.videoTitle ?? item.title}</div>
                <VideoPlayer src={item.videoUrl} title={item.title} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
